import { inject, Injectable, signal } from '@angular/core';

import { differenceInMinutes } from 'date-fns';
import { map, Observable, of, switchMap, tap } from 'rxjs';

import { IGetDriverTachographFileSchedule, IGetTachographDriverFileStatus } from '@src/app/interfaces/api/files-ddd.interface';
import { FilesDddService } from '@src/app/services/api/files-ddd.service';
import { sortRoutesByDate as sortListByDate } from '@src/app/utils/date';
import { WorkerService } from '@layout/driver/services/worker.service';

type DriverFileStatus = Map<number, IGetTachographDriverFileStatus>;

@Injectable({
  providedIn: 'root'
})
export class FascadeTachographDriverFileStatusService {
  private readonly workerService = inject(WorkerService);
  private readonly filesDDDService = inject(FilesDddService);

  public driverList = signal<IGetDriverTachographFileSchedule[]>([]);
  public vehicleFileStatusList: DriverFileStatus = new Map([]);

  private lastTimeDownload: Date = null!;

  constructor() { }

  public GetDriverTachographFileSchedule(instantGet?: boolean): Observable<IGetDriverTachographFileSchedule[]> {
    const minutesPassed = this.lastTimeDownload && differenceInMinutes(new Date(), this.lastTimeDownload);

    if (!this.lastTimeDownload || minutesPassed || instantGet) {
      this.lastTimeDownload = new Date();
      return this.SetTachographDriverFileStatus().pipe(
        switchMap(() => this.filesDDDService.GetDriverTachographFileSchedule()),
        map(res => {
          return sortListByDate(
            res.Result.Schedules.map(
              item => ({
                ...item,
                Driver: this.workerService.getWorkerById(item.DriverId),
                FilesStatus: this.vehicleFileStatusList.get(item.DriverId)!,
              })
            ),
            'MandatedLoadDate',
          )
          .reverse();
        }),
        tap((data) => {
          this.driverList.set(data);
        }),
      );
    }

    return of(this.driverList());
  }

  private SetTachographDriverFileStatus(): Observable<DriverFileStatus> {
    return this.filesDDDService.GetTachographDriverFileStatus().pipe(
      tap((res) => this.vehicleFileStatusList = new Map(res.map(item => [item.WorkerId, item]))),
      map(() => this.vehicleFileStatusList),
    );
  }

  public ForceDriverFile(driverId: number): Observable<IGetDriverTachographFileSchedule[]> {
    return this.filesDDDService.ForceFileDownloadForDriver(driverId).pipe(
      switchMap(() => this.GetDriverTachographFileSchedule(true)),
    );
  }

  public SetTachographScheduleForDrivers(driverIds: number[], isAdd: boolean = true): Observable<void> {
    return this.filesDDDService.SetTachographScheduleForDrivers(driverIds, isAdd);
  }

}

